import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { gameCategories } from '@/mocks/home';
import JsonLd from '@/components/base/JsonLd';

const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '开云棋牌游戏分类',
  description: '汇集斗地主、麻将、德州扑克、百家乐、牛牛、龙虎斗等经典棋牌游戏',
  isPartOf: {
    '@type': 'WebSite',
    name: '开云棋牌',
    url: 'https://kaiyunqipai.com',
  },
};

export default function Categories() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredGames =
    activeCategory === 'all'
      ? gameCategories
      : gameCategories.filter((g) => g.id === activeCategory);

  useEffect(() => {
    document.title = '游戏分类 - 开云棋牌 | 斗地主、麻将、德州扑克等经典棋牌游戏';
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <JsonLd data={collectionSchema} />
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <ol className="flex items-center gap-1.5 py-3 text-xs md:text-sm flex-wrap">
              <li>
                <Link to="/" className="text-foreground-400 hover:text-primary-500 transition-colors">
                  首页
                </Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700 font-medium">游戏分类</li>
            </ol>
          </div>
        </nav>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 mb-4">
                <i className="ri-gamepad-line text-primary-500 text-sm"></i>
                <span className="text-primary-600 text-xs font-medium">所有游戏</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                游戏<span className="text-primary-600">分类</span>
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                汇集国内外经典棋牌玩法，选择你喜欢的游戏开始对战
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-12">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部游戏
              </button>
              {gameCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-52 md:h-60 overflow-hidden">
                    <img
                      src={game.image}
                      alt={game.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-lg bg-background-50/20 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${game.icon} text-background-50 text-lg`}></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-background-50 font-heading">
                            {game.name}
                          </h3>
                          <span className="text-background-50/70 text-xs">
                            {game.playerCount} · 难度{game.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                      {game.description}
                    </p>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="flex items-center gap-1 text-accent-500 text-xs font-medium">
                        <i className="ri-fire-line"></i>
                        热度 {game.popularity}%
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {game.features.map((f, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs font-medium"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                    <a
                      href="#download"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line"></i>
                      开始游戏
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}